import axios, { AxiosResponse, isAxiosError } from "axios";
import { Paginated, RequestError, setFile } from "./api";

const url = `${process.env.REACT_APP_URL}/contratos`;

export interface Contrato {
  id: number;
  nome: string;
  contratoLote: string;
  descricao: string;
  idEmpreendimento?: string;
  idBarragem?: number;

  status: boolean;
  validade: Date;

  idUpdate?: string;
  dataCadastro: Date;
  dataModificacao?: Date;

  file: string;
}

const getContratoById = async (id: number): Promise<Contrato | RequestError> => {
  try {
    const data: AxiosResponse<Contrato> = await axios.get(`${url}/${id}`);
    return processContrato(data.data);
  }
  catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Contrato Error",
        message: error.response?.data.message ?? "Erro ao obter dados do contrato",
      };
    } else {
      throw error;
    }
  }
};

const getAll = async (page: number, perPage: number): Promise<Paginated<number> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<number>> = await axios.get(`${url}/all/${page}/${perPage}`);
    return data.data;
  }
  catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Contrato Error",
        message: error.response?.data.message ?? "Erro ao obter dados do contrato",
      };
    } else {
      throw error;
    }
  }
};

const getContratosByEmpreendimento = async (empreendimento: string, page: number, perPage: number): Promise<Paginated<number> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<number>> = await axios.get(`${url}/empreedimento/${empreendimento}/${page}/${perPage}`);
    return data.data;
  }
  catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Contrato Error",
        message: error.response?.data.message ?? "Erro ao obter dados do contrato",
      };
    } else {
      throw error;
    }
  }
};

const getContratosByEmpreendimentoAndDam = async (empreendimento: string, dam: number, page: number, perPage: number): Promise<Paginated<number> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<number>> = await axios.get(`${url}/empreedimento_and_dam/${empreendimento}/${dam}/${page}/${perPage}`);
    return data.data;
  }
  catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Contrato Error",
        message: error.response?.data.message ?? "Erro ao obter dados do contrato",
      };
    } else {
      throw error;
    }
  }
};

const novoContrato = async (newContrato: Contrato, file: File, onProgess?: (percent: number) => void): Promise<Contrato | RequestError> => {
  try {
    const dados = {
      'nome': newContrato.nome,
      'contrato_lote': newContrato.contratoLote,
      'descricao': newContrato.descricao,
      'id_empreendimento': newContrato.idEmpreendimento,
      'id_barragem': newContrato.idBarragem,
      'validade': newContrato.validade.toISOString(),
    };

    const data: AxiosResponse<Contrato> = await axios.post(`${url}`, dados);

    const contrato = processContrato(data.data);

    const fileUpload = setFile<Contrato>(
      `${url}/file_upload_parts/${contrato.id}`,
      `${url}/file_upload_complete/${contrato.id}`,
      file, onProgess
    );
    if ("error" in fileUpload) return fileUpload;

    return contrato;
  }
  catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Contrato Error",
        message: error.response?.data.message ?? "Erro ao obter dados do contrato",
      };
    } else {
      throw error;
    }
  }
};

const setFinalized = async (id: number, status: boolean): Promise<Contrato | RequestError> => {
  try {
    const data: AxiosResponse<Contrato> = await axios.post(
      `${url}/set_finalized`,
      {
        'id': id,
        'status': status,
      },
    );

    return processContrato(data.data);
  }
  catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Contrato Error",
        message: error.response?.data.message ?? "Erro ao obter dados do contrato",
      };
    } else {
      throw error;
    }
  }
};

const setEmpreendimento = async (id: number, idEmpreendimento: string): Promise<Contrato | RequestError> => {
  try {
    const data: AxiosResponse<Contrato> = await axios.post(`${url}/set_empreendimento/${id}/${idEmpreendimento}`);
    return processContrato(data.data);
  }
  catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Contrato Error",
        message: error.response?.data.message ?? "Erro ao setar o emrpeendimento no contrato.",
      };
    } else {
      throw error;
    }
  }
};

const setBarragem = async (id: number, idBarragem: number): Promise<Contrato | RequestError> => {
  try {
    const data: AxiosResponse<Contrato> = await axios.post(`${url}/set_barragem/${id}/${idBarragem}`);
    return processContrato(data.data);
  }
  catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Contrato Error",
        message: error.response?.data.message ?? "Erro ao setar a barragem no contrato.",
      };
    } else {
      throw error;
    }
  }
};



function processContrato(value: Contrato): Contrato {
  value.dataCadastro = new Date(value.dataCadastro);
  value.dataModificacao &&= new Date(value.dataModificacao);
  value.validade = new Date(value.validade);

  return value;
}

export const ApiContrato = {
  setEmpreendimento,
  setBarragem,
  getContratoById,
  getAll,
  getContratosByEmpreendimento,
  getContratosByEmpreendimentoAndDam,
  novoContrato,
  setFinalized,
};